import * as TSU from "@panyam/tsutils";
import { ZERO, Atom, LeafAtom, Group, AtomType } from "./core";

/**
 * Represents an item to be positioned in collision-based layout.
 */
export interface CollisionLayoutItem {
  /** Time offset as a fraction (numerator/denominator) */
  timeOffset: TSU.Num.Fraction;
  /** Duration of this item */
  duration: TSU.Num.Fraction;
  /** Width of pre-embellishments (extends left from glyph position) */
  glyphOffset: number;
  /** Minimum width of the item (includes all embellishments and glyph) */
  minWidth: number;
}

/**
 * Result of collision-based layout for a single item.
 */
export interface CollisionLayoutResult {
  /** The calculated x position for the item */
  x: number;
  /** Whether the item was pushed right due to collision */
  wasCollision: boolean;
}

/**
 * Computes collision-based positions for a sequence of items within a container.
 *
 * ## Algorithm
 *
 * 1. Calculate ideal glyph position: `glyphX = (timeOffset / totalDuration) * containerWidth`
 * 2. Pre-embellishments extend left: `realX = glyphX - glyphOffset`
 * 3. Collision check: if `realX < prevItemEndX`, then `realX = prevItemEndX`
 * 4. Track: `prevItemEndX = realX + minWidth`
 *
 * @param items Items to position (must be in time order)
 * @param totalDuration Total duration of all items
 * @param containerWidth Width of the container to position items within
 * @returns Array of positions for each item
 */
export function computeCollisionLayout(
  items: CollisionLayoutItem[],
  totalDuration: TSU.Num.Fraction,
  containerWidth: number,
): CollisionLayoutResult[] {
  const results: CollisionLayoutResult[] = [];
  let prevItemEndX = 0;
  let currTime = ZERO;

  for (const item of items) {
    // 1. Calculate ideal glyph position based on time offset
    const glyphX = totalDuration.isZero ? 0 : currTime.timesNum(containerWidth).divby(totalDuration).floor;

    // 2. Pre-embellishments extend left from glyph position
    let realX = glyphX - item.glyphOffset;

    // 3. Collision check: push right if overlapping previous item
    const wasCollision = realX < prevItemEndX;
    if (wasCollision) {
      realX = prevItemEndX;
    }

    results.push({ x: realX, wasCollision });

    // 4. Track end position for next collision check
    prevItemEndX = realX + item.minWidth;

    currTime = currTime.plus(item.duration);
  }

  return results;
}

/**
 * Base class for all renderable objects.
 *
 * Shape caches properties like bounding boxes to improve performance,
 * since bounding box calculations can be expensive. This also allows
 * testing layouts and positioning without worrying about implementation details.
 */
export abstract class Shape {
  private static idCounter = 0;
  readonly shapeId: number = Shape.idCounter++;

  /**
   * Note that x and y coordinates are not always the x and y coordinates
   * of the bounding box.
   * E.g., a circle's x and y coordinates are its center point and not the
   * top left corner.
   * These "main" coordinates are referred to as control coordinates.
   */
  protected _x: number | null = null;
  protected _y: number | null = null;
  protected _width: number | null = null;
  protected _height: number | null = null;
  protected _bbox: TSU.Geom.Rect;
  protected _minSize: TSU.Geom.Size;
  protected parentShape: Shape | null = null;
  /** Child shapes contained within this shape */
  children: Shape[] = [];

  /**
   * Gets the bounding box of this shape.
   * Calculates it if it hasn't been calculated yet.
   */
  get bbox(): TSU.Geom.Rect {
    if (!this._bbox) {
      this._bbox = this.refreshBBox();
    }
    return this._bbox;
  }

  /**
   * Gets the minimum size of this shape.
   * This is usually the size of the bounding box.
   */
  get minSize(): TSU.Geom.Size {
    if (!this._minSize) {
      this._minSize = this.refreshMinSize();
    }
    return this._minSize;
  }

  /**
   * Refreshes the bounding box of this shape.
   * Called when the shape knows the bbox it is tracking cannot be trusted
   * and has to be refreshed by calling native methods.
   * @returns The refreshed bounding box
   */
  protected abstract refreshBBox(): TSU.Geom.Rect;

  /**
   * Refreshes the minimum size of this shape.
   * @returns The refreshed minimum size
   */
  protected abstract refreshMinSize(): TSU.Geom.Size;

  /**
   * Updates the bounds of this shape.
   * @param x New x coordinate, or null to keep current value
   * @param y New y coordinate, or null to keep current value
   * @param w New width, or null to keep current value
   * @param h New height, or null to keep current value
   * @returns The updated bounds values
   */
  protected abstract updateBounds(
    x: null | number,
    y: null | number,
    w: null | number,
    h: null | number,
  ): [number | null, number | null, number | null, number | null];

  /**
   * Invalidates the cached bounds of this shape.
   * Forces recalculation of bounding box and minimum size.
   */
  invalidateBounds(): void {
    this._minSize = null as unknown as TSU.Geom.Size;
    this._bbox = null as unknown as TSU.Geom.Rect;
  }

  /**
   * Sets the bounds of this shape.
   *
   * Note that null and NaN are valid values and mean the following:
   * - null: Don't change the value
   * - NaN: Set the value to null (use the bounding box's value)
   *
   * @param x New x coordinate, or null to keep current value
   * @param y New y coordinate, or null to keep current value
   * @param w New width, or null to keep current value
   * @param h New height, or null to keep current value
   * @param applyLayout Whether to apply layout immediately
   * @returns The updated bounds values
   */
  setBounds(
    x: number | null,
    y: number | null,
    w: number | null,
    h: number | null,
    applyLayout = false,
  ): [number | null, number | null, number | null, number | null] {
    if (x != null) {
      if (isNaN(x)) {
        this._x = null;
      } else {
        this._x = x;
      }
    }
    if (y != null) {
      if (isNaN(y)) {
        this._y = null;
      } else {
        this._y = y;
      }
    }
    if (w != null) {
      if (isNaN(w)) {
        this._width = null;
      } else {
        this._width = w;
      }
    }
    if (h != null) {
      if (isNaN(h)) {
        this._height = null;
      } else {
        this._height = h;
      }
    }
    const [nx, ny, nw, nh] = this.updateBounds(x, y, w, h);
    if (nx != null) {
      if (isNaN(nx)) {
        this._x = null;
      } else {
        this._x = nx;
      }
    }
    if (ny != null) {
      if (isNaN(ny)) {
        this._y = null;
      } else {
        this._y = ny;
      }
    }
    if (nw != null) {
      if (isNaN(nw)) {
        this._width = null;
      } else {
        this._width = nw;
      }
    }
    if (nh != null) {
      if (isNaN(nh)) {
        this._height = null;
      } else {
        this._height = nh;
      }
    }
    if (applyLayout) this.refreshLayout();
    // this.resetBBox();
    return [nx, ny, nw, nh];
  }

  /**
   * Checks if this shape has an explicit x coordinate.
   */
  get hasX(): boolean {
    return this._x != null && !isNaN(this._x);
  }

  /**
   * Checks if this shape has an explicit y coordinate.
   */
  get hasY(): boolean {
    return this._y != null && !isNaN(this._y);
  }

  /**
   * Checks if this shape has an explicit width.
   */
  get hasWidth(): boolean {
    return this._width != null && !isNaN(this._width);
  }

  /**
   * Checks if this shape has an explicit height.
   */
  get hasHeight(): boolean {
    return this._height != null && !isNaN(this._height);
  }

  /**
   * Gets the x coordinate within the parent's coordinate system.
   */
  get x(): number {
    return this._x || 0;
  }

  /**
   * Sets the x coordinate within the parent's coordinate system.
   */
  set x(x: number | null) {
    // Here a manual x is being set - how does this interfere with the bounding box?
    // We should _x to the new value to indicate a manual value was set.
    // and reset bbox so that based on this x a new bbox may need to be calculated
    this.setBounds(x == null ? NaN : x, null, null, null);
  }

  /**
   * Gets the y coordinate within the parent's coordinate system.
   */
  get y(): number {
    if (this._y != null) return this._y;
    return 0; // this.bbox.y;
  }

  /**
   * Sets the y coordinate within the parent's coordinate system.
   */
  set y(y: number | null) {
    this.setBounds(null, y == null ? NaN : y, null, null);
  }

  /**
   * Gets the width of this shape.
   */
  get width(): number {
    if (this._width != null) return this._width;
    return 0; // this.bbox.width;
  }

  /**
   * Sets the width of this shape.
   */
  set width(w: number | null) {
    this.setBounds(null, null, w == null ? NaN : w, null);
  }

  /**
   * Gets the height of this shape.
   */
  get height(): number {
    if (this._height != null) return this._height;
    return 0; // this.bbox.height;
  }

  /**
   * Sets the height of this shape.
   */
  set height(h: number | null) {
    this.setBounds(null, null, null, h == null ? NaN : h);
  }

  /**
   * Refreshes the layout of this shape.
   * Called when bounds or other properties have changed to give the shape an
   * opportunity to layout its children. For shapes with no children this is a no-op.
   */
  refreshLayout(): void {
    // throw new Error("Implement this");
  }
}

/**
 * Represents an embellishment applied to a musical element.
 */
export abstract class Embelishment extends Shape {}

/**
 * A shape that wraps an SVG element.
 * ElementShape provides the base class for all shapes that are rendered as SVG elements.
 */
export class ElementShape<T extends SVGGraphicsElement = SVGGraphicsElement> extends Shape {
  /**
   * Creates a new ElementShape.
   * @param element The SVG element this shape wraps
   */
  constructor(public readonly element: T) {
    super();
  }

  /**
   * Refreshes the bounding box of this element.
   * @returns The refreshed bounding box
   */
  protected refreshBBox(): TSU.Geom.Rect {
    return TSU.DOM.svgBBox(this.element);
  }

  /**
   * Refreshes the minimum size of this element.
   * @returns The refreshed minimum size
   */
  protected refreshMinSize(): TSU.Geom.Size {
    return TSU.DOM.svgBBox(this.element);
  }

  /**
   * Updates the bounds of this element.
   * @param x New x coordinate, or null to keep current value
   * @param y New y coordinate, or null to keep current value
   * @param w New width, or null to keep current value
   * @param h New height, or null to keep current value
   * @returns The updated bounds values
   */
  protected updateBounds(
    x: null | number,
    y: null | number,
    w: null | number,
    h: null | number,
  ): [number | null, number | null, number | null, number | null] {
    return [x, y, w, h];
  }

  /**
   * Refreshes the layout of this element.
   * Updates the element's attributes based on the shape's properties.
   */
  refreshLayout(): void {
    if (this.hasX) this.element.setAttribute("x", "" + this._x);
    if (this.hasY) this.element.setAttribute("y", "" + this._y);
  }
}

/**
 * Base class for views that represent atoms in the notation.
 * AtomView provides the visual representation of an atom.
 */
export abstract class AtomView extends Shape {
  /** Nesting depth of this atom in the structure */
  depth = 0;
  /** Index of the role containing this atom */
  roleIndex = 0;

  // LayoutMetrics for the AtomView so all atomviews laid out on the
  // same baseline will show up aligned vertically
  /** Baseline position for vertical alignment */
  baseline: number;
  /** Ascent (space above baseline) */
  ascent: number;
  /** Descent (space below baseline) */
  descent: number;
  /** Height of capital letters */
  capHeight: number;
  /** Space between lines */
  leading: number;

  /**
   * Checks if this atom view represents a leaf atom.
   */
  abstract isLeaf(): boolean;

  abstract get totalDuration(): TSU.Num.Fraction;

  /**
   * Returns the horizontal offset from the atom's origin to where the note glyph starts.
   * This accounts for left embellishments that appear before the note.
   * Used by GroupView to align note glyphs at their correct time positions.
   *
   * Default is 0 (glyph starts at origin). Subclasses with left embellishments
   * should override to return the width of left-side decorations.
   */
  get glyphOffset(): number {
    return 0;
  }

  /**
   * Creates the SVG elements needed for this atom view.
   * @param parent The parent SVG element to attach to
   */
  abstract createElements(parent: SVGGraphicsElement): void;
}

/**
 * A view for leaf atoms (those that cannot contain other atoms).
 */
export abstract class LeafAtomView extends AtomView {
  /**
   * Creates a new LeafAtomView.
   * @param leafAtom The leaf atom this view represents
   */
  constructor(public leafAtom: LeafAtom) {
    super();
  }

  /**
   * Leaf atom views always return true for isLeaf().
   */
  isLeaf(): boolean {
    return true;
  }

  /**
   * Gets a unique identifier for this view based on the atom's UUID.
   */
  get viewId(): number {
    return this.leafAtom.uuid;
  }

  /**
   * Returns the total duration of the atom rendered by this view.
   */
  get totalDuration(): TSU.Num.Fraction {
    return this.leafAtom.duration;
  }
}

/**
 * A view for group atoms that contain multiple child atoms.
 */
export abstract class GroupView extends AtomView {
  /** Space between atoms in this group */
  protected atomSpacing: number;
  /** The SVG group element for this view */
  groupElement: SVGGElement;
  /** Views for the atoms in this group */
  protected atomViews: AtomView[] = [];
  private _embelishments: Embelishment[];
  /** Whether this group represents notes by default */
  defaultToNotes = true;
  /** Whether this view needs layout */
  needsLayout = true;
  /** Scale factor for this group */
  scaleFactor = 1.0;
  /**
   * When true, shows continuation markers (",") for atoms with duration > 1
   * instead of just leaving empty space.
   * Disabled by default as group bracket lines provide clearer visual boundaries.
   */
  showContinuationMarkers = false;
  /** SVG elements for continuation markers */
  protected continuationMarkerElements: SVGTextElement[] = [];
  /** Actual content width after layout (position of last atom + its width) */
  contentWidth = 0;

  /**
   * Creates a new GroupView.
   * @param group The group atom this view represents
   * @param config Optional configuration object
   */
  constructor(
    public group: Group,
    config?: any,
  ) {
    super();
    this.atomSpacing = 2;
    this.setStyles(config || {});
  }

  /**
   * Returns the total duration of the group rendered by this view.
   * This returns the group's actual duration (accounting for scaling),
   * not the raw sum of child durations.
   */
  get totalDuration(): TSU.Num.Fraction {
    return this.group.duration;
  }

  /**
   * Creates the SVG elements needed for this group view.
   * @param parent The parent SVG element to attach to
   */
  createElements(parent: SVGGraphicsElement): void {
    this.groupElement = TSU.DOM.createSVGNode("g", {
      parent: parent,
      attrs: {
        class: "groupViewRoot",
        id: "groupViewRoot" + this.group.uuid,
      },
    });

    // now create child atom views for each atom in this Group
    // Skip markers - they're rendered in separate pre/post marker columns
    for (const atom of this.group.atoms.values()) {
      if (atom.TYPE === AtomType.MARKER) continue;
      const atomView = this.createAtomView(atom);
      this.atomViews.push(atomView);
    }
    this.invalidateBounds();
  }

  /**
   * Group views always return false for isLeaf().
   */
  isLeaf(): boolean {
    return false;
  }

  /**
   * Refreshes the bounding box of this group.
   * @returns The refreshed bounding box
   */
  protected refreshBBox(): TSU.Geom.Rect {
    return TSU.DOM.svgBBox(this.groupElement);
  }

  /**
   * Refreshes the minimum size of this group using duration-based width calculation.
   *
   * ## Duration-Based Width Algorithm
   *
   * This algorithm ensures atoms with extended durations receive proportionally
   * more horizontal space. For example, with `\beatDuration(4)` and input `S 2 R G M`:
   * - S has duration 1, R has duration 2, G has duration 1
   * - R should visually occupy twice the horizontal space of S or G
   *
   * ### Algorithm Steps:
   *
   * 1. **Calculate width per duration unit**: For each atom, compute the visual width
   *    needed per unit of duration: `(visualWidth + spacing) / duration`
   *
   * 2. **Find maximum**: Take the maximum width-per-duration across all atoms.
   *    This ensures every atom has enough space for its visual content.
   *
   * 3. **Scale by total duration**: Multiply the max width-per-duration by the
   *    group's total duration to get the final group width.
   *
   * ### Example:
   * ```
   * Atoms: S(dur=1, width=10px), R(dur=2, width=10px), G(dur=1, width=10px)
   * Spacing: 5px
   *
   * Width per duration:
   *   S: (10 + 5) / 1 = 15 px/unit
   *   R: (10 + 5) / 2 = 7.5 px/unit
   *   G: (10 + 5) / 1 = 15 px/unit
   *
   * Max width per duration: 15 px/unit
   * Total duration: 1 + 2 + 1 = 4 units
   * Group width: 15 * 4 = 60px
   *
   * Positioning:
   *   S at x=0 (time 0/4 * 60 = 0)
   *   R at x=15 (time 1/4 * 60 = 15)
   *   G at x=45 (time 3/4 * 60 = 45)
   * ```
   *
   * @returns The refreshed minimum size
   */
  protected refreshMinSize(): TSU.Geom.Size {
    let maxHeight = 0;

    // Step 1: Calculate width per duration unit for each atom
    let minWidthPerDuration = 0;
    this.atomViews.forEach((av) => {
      const ms = av.minSize;
      const dur = av.totalDuration;
      if (!dur.isZero) {
        const durValue = dur.num / dur.den;
        const widthPerDur = (ms.width + this.atomSpacing) / durValue;
        // Step 2: Track maximum width per duration
        minWidthPerDuration = Math.max(minWidthPerDuration, widthPerDur);
      }
      maxHeight = Math.max(maxHeight, ms.height);
    });

    // Step 3: Scale by total duration
    const totalDuration = this.group.totalChildDuration;
    const totalDurValue = totalDuration.num / totalDuration.den;
    // Subtract one atomSpacing since we don't need spacing after the last atom
    const totalWidth = Math.max(0, minWidthPerDuration * totalDurValue - this.atomSpacing);

    return new TSU.Geom.Size(totalWidth * this.scaleFactor, maxHeight * this.scaleFactor);
  }

  /**
   * Creates an atom view for a specific atom.
   * @param atom The atom to create a view for
   * @returns The created atom view
   */
  abstract createAtomView(atom: Atom): AtomView;

  /**
   * Updates the bounds of this group.
   * @param x New x coordinate, or null to keep current value
   * @param y New y coordinate, or null to keep current value
   * @param w New width, or null to keep current value
   * @param h New height, or null to keep current value
   * @returns The updated bounds values
   */
  protected updateBounds(
    x: null | number,
    y: null | number,
    w: null | number,
    h: null | number,
  ): [number | null, number | null, number | null, number | null] {
    return [x, y, w, h];
  }

  /**
   * Refreshes the layout of this group using collision-based positioning.
   *
   * ## Collision-Based Layout Algorithm
   *
   * Atoms are positioned using time-based positioning with collision avoidance.
   * Each atom starts at its ideal time-based position, but is pushed right if
   * it would overlap with the previous atom (including embellishments).
   *
   * ### Algorithm:
   * ```
   * 1. Calculate ideal glyph position: glyphX = (time / totalDuration) * groupWidth
   * 2. Pre-embellishments extend left: realX = glyphX - preEmbellishmentWidth
   * 3. Collision check: if (realX < prevNoteEndX) realX = prevNoteEndX
   * 4. Position the atom at realX
   * 5. Track: prevNoteEndX = realX + atom.minSize.width
   * ```
   *
   * ### Width Source Priority:
   *
   * 1. **Column width** (preferred): If width was set via `setBounds()` from the
   *    grid layout system (ColAlign), use that width. This enables global alignment
   *    across all beats in the same column.
   *
   * 2. **Minimum size**: Fall back to `minSize.width` calculated by `refreshMinSize()`.
   *
   * ### Continuation Markers:
   *
   * When `showContinuationMarkers` is true (default), atoms with duration > 1
   * will have "," markers rendered at each additional time slot. For example,
   * an atom with duration 2 will show "R ," instead of "R   ".
   *
   * ### Example (no collisions):
   * ```
   * Input: S R G M (equal duration, no embellishments)
   * Group width: 60px, Total duration: 4 units
   *
   * Positioning:
   *   S at x=0   (time 0/4 * 60 = 0)
   *   R at x=15  (time 1/4 * 60 = 15)
   *   G at x=30  (time 2/4 * 60 = 30)
   *   M at x=45  (time 3/4 * 60 = 45)
   * ```
   *
   * ### Example (with collision):
   * ```
   * Input: [Jaaru+S] R (S has 10px pre-embellishment, each atom 15px wide)
   * Group width: 60px, Total duration: 2 units
   *
   * Without collision avoidance:
   *   S.glyphX = 0, S.realX = 0 - 10 = -10 (clamped to 0)
   *   R.glyphX = 30, R overlaps with S
   *
   * With collision avoidance:
   *   S at x=0 (prevNoteEndX becomes 15)
   *   R.glyphX = 30, R.realX = 30 - 0 = 30
   *   30 >= 15, no collision, R at x=30
   * ```
   */
  refreshLayout(): void {
    let transform = "translate(" + this.x + "," + this.y + ")";
    if (this.scaleFactor < 1) {
      transform += " scale(" + this.scaleFactor + ")";
    }
    this.groupElement.setAttribute("transform", transform);

    // BASELINE ALIGNMENT: Position atoms at bottom of allocated height.
    // This ensures atoms align across beats with different bracket heights.
    // When beats in the same row have different content heights (e.g., due to
    // nested group brackets), all beats get the same allocated height from the
    // grid. By positioning content at the bottom, atom baselines align.
    const unscaledContentHeight = this.minSize.height / this.scaleFactor;
    const unscaledAllocatedHeight = this.hasHeight ? this.height / this.scaleFactor : unscaledContentHeight;
    const currY = unscaledAllocatedHeight - unscaledContentHeight;

    const totalDur = this.group.totalChildDuration;

    // Width source priority: column width (for global alignment) > minSize
    const unscaledMinWidth = this.minSize.width / this.scaleFactor;
    const groupWidth = this.hasWidth ? this.width / this.scaleFactor : unscaledMinWidth;

    // Clear existing continuation markers before re-rendering
    this.clearContinuationMarkers();

    // Position each atom using collision-based layout
    // Atoms start at their time-based position, but are pushed right if they would
    // overlap with the previous atom (including embellishments)
    let currTime = ZERO;
    let prevNoteEndX = 0;
    this.atomViews.forEach((av, index) => {
      // 1. Calculate ideal glyph position based on time offset
      const glyphX = totalDur.isZero ? 0 : currTime.timesNum(groupWidth).divby(totalDur).floor;

      // 2. Pre-embellishments extend left from glyph position
      //    realX is where the atom origin should be placed
      let realX = glyphX - av.glyphOffset;

      // 3. Collision check: push right if overlapping previous atom
      if (realX < prevNoteEndX) {
        realX = prevNoteEndX;
      }

      // 4. Position the atom
      av.setBounds(realX, currY, null, null, true);

      // 5. Track end position for next collision check
      // For groups, use contentWidth (actual content) instead of bbox.width (which may include extra space).
      // For leaf atoms, use bbox.width.
      const avWidth = (av as any).contentWidth || av.bbox.width;
      prevNoteEndX = realX + avWidth;

      // Render continuation markers for atoms with duration > 1
      if (this.showContinuationMarkers && !totalDur.isZero) {
        const atomDur = av.totalDuration;
        const durValue = atomDur.num / atomDur.den;
        if (durValue > 1) {
          // Render one marker at each additional time slot within the atom's duration
          const numMarkers = Math.floor(durValue) - 1;
          for (let i = 1; i <= numMarkers; i++) {
            // Marker time = currTime + (atomDuration * i / floor(duration))
            const markerTime = currTime.plus(atomDur.timesNum(i).divbyNum(Math.floor(durValue)));
            const markerX = markerTime.timesNum(groupWidth).divby(totalDur).floor;
            this.renderContinuationMarker(markerX, currY);
          }
        }
      }

      currTime = currTime.plus(av.totalDuration);
    });

    // Track actual content width for bracket sizing
    this.contentWidth = prevNoteEndX;

    this.invalidateBounds();
    for (const e of this.embelishments) e.refreshLayout();
    this.invalidateBounds();
  }

  /**
   * Clears all continuation marker elements.
   */
  protected clearContinuationMarkers(): void {
    for (const el of this.continuationMarkerElements) {
      el.remove();
    }
    this.continuationMarkerElements = [];
  }

  /**
   * Renders a continuation marker (",") at the specified position.
   * @param x X position for the marker
   * @param y Y position for the marker
   */
  protected renderContinuationMarker(x: number, y: number): void {
    const marker = TSU.DOM.createSVGNode("text", {
      parent: this.groupElement,
      attrs: {
        class: "continuationMarker",
        x: x.toString(),
        y: y.toString(),
      },
      text: ",",
    }) as SVGTextElement;
    this.continuationMarkerElements.push(marker);
  }

  /**
   * Gets the embellishments for this group.
   */
  get embelishments(): Embelishment[] {
    if (!this._embelishments) {
      this._embelishments = this.createEmbelishments();
    }
    return this._embelishments;
  }

  /**
   * Creates the embellishments for this group.
   * @returns An array of embellishments
   */
  protected createEmbelishments(): Embelishment[] {
    return [];
  }

  /**
   * Sets the styles for this group.
   * @param config Style configuration object
   */
  setStyles(config: any): void {
    if ("atomSpacing" in config) this.atomSpacing = config.atomSpacing;
    if ("showContinuationMarkers" in config) this.showContinuationMarkers = config.showContinuationMarkers;
    this.needsLayout = true;
  }
}
